import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createHttpOwnerListsApi, OwnerListRequestError, type ProductDetail } from "../../api/ownerListsApi";
import { AreaHead, AreaPage, EmptyState } from "../area";
import { OrdersView } from "./OrdersView";
import type { BuilderApi, ShopApi, ShopIndex } from "../../api/contract";
import type { ShopSection } from "../../lib/shop";
import { CategoriesView } from "./CategoriesView";
import { ProductEditor } from "./ProductEditor";
import { ProductsList } from "./ProductsList";
import { ShopSettingsView } from "./SettingsView";
import { ProductEditorSkeleton, ShopActivity, ShopLoadError } from "./ShopLoading";
import { useShopLoad } from "./useShopLoad";
import { shopRequest } from "./shopRequest";

interface ShopViewProps {
  api: ShopApi;
  builderApi?: BuilderApi;
  siteId: string;
  shop: ShopIndex;
  onSaved: (next: ShopIndex) => void;
  section: ShopSection;
  onSection: (next: ShopSection) => void;
  productId: string | null;
  orderId?: string | null;
  onOrderId?: (id: string | null) => void;
  orderChangeId?: string;
  onSelectedOrder?: (order: { id: string; number: number } | null) => void;
  onProductId: (id: string | null) => void;
  /** The site's address without the scheme, for the web address field. */
  address: string;
  publicOrigin: string;
  language: string;
  timeZone?: string | null;
  previewRoot?: string;
  /** The shop's pages are designed in the Website, with Stax. */
  onDesignPages: () => void;
  onProductShown?: (item: { name: string; slug: string } | null) => void;
  onDirtyChange?: (dirty: boolean) => void;
}

/** The Shop area: the screen the side menu picks. A product opens its editor in place of the list. */
export function ShopView({ api, builderApi, siteId, shop, onSaved, section, productId, onProductId, onSelectedOrder, orderId, onOrderId, orderChangeId, address, publicOrigin, language, timeZone, onDesignPages, onDirtyChange, previewRoot, onProductShown }: ShopViewProps) {
  const [orderOpen, setOrderOpen] = useState(false);
  useEffect(() => { setOrderOpen(false); }, [section]);
  const lists = useMemo(() => api.lists ?? createHttpOwnerListsApi(), [api]);
  const currentProduct = useRef(productId); currentProduct.current = productId;
  const back = () => { currentProduct.current = null; onProductId(null); };
  const loadProduct = useCallback((signal: AbortSignal): Promise<ProductDetail> => lists.product(siteId, productId!, signal), [lists, siteId, productId]);
  const loaded = useShopLoad(loadProduct, productId ? `${siteId}/${productId}/${shop.version}` : null, productId ? `${siteId}/${productId}` : undefined);
  const detail = loaded.data;
  useEffect(() => {
    onProductShown?.(detail ? { name: detail.product.details.name, slug: detail.product.details.slug } : null);
  }, [detail, onProductShown]);
  const reload = async () => {
    const next = await shopRequest(signal => api.getShop(siteId, signal));
    const id = currentProduct.current;
    if (id) {
      try { const nextDetail = await shopRequest(signal => lists.product(siteId, id, signal)); if (currentProduct.current === id && productId === id) loaded.replace(nextDetail); }
      catch (e) { if (e instanceof OwnerListRequestError && e.status === 404 && currentProduct.current === id) back(); else throw e; }
    }
    onSaved(next);
  };
  const product = detail?.product.id === productId ? detail.product : null;
  const editorShop = detail ? { ...shop, products: [detail.product], stock: detail.stock, files: detail.files } : shop;
  return <AreaPage area="shop">
    {!productId && !orderOpen && <AreaHead designLabel="Design shop pages" onDesign={onDesignPages} />}
    {product ? <><ProductEditor key={product.id} api={api} builderApi={builderApi} onDirtyChange={onDirtyChange} siteId={siteId} shop={editorShop} product={product} language={language} reload={reload} onBack={back} />{loaded.busy && <ShopActivity label="Updating product…" />}{loaded.error && <ShopLoadError message={loaded.error} retry={loaded.retry} />}</>
      : productId ? <><button className="bw-btn bw-btn--text" onClick={back}>Back to Products</button>{loaded.error ? <ShopLoadError message={loaded.error} retry={loaded.retry} /> : <ProductEditorSkeleton />}</>
      : section === "settings" ? <ShopSettingsView key="settings" api={api} siteId={siteId} shop={shop} onSaved={onSaved} address={address} timeZone={timeZone} onDirty={onDirtyChange} />
      : section === "categories" ? <CategoriesView previewRoot={previewRoot} api={api} siteId={siteId} shop={shop} onSaved={onSaved} />
      : section === "orders" ? api.orders ? <OrdersView orderId={orderId} onOrderId={onOrderId} api={api.orders} siteId={siteId} publicOrigin={publicOrigin} language={language} timeZone={timeZone} onOpenChange={setOrderOpen} onSelectedOrder={onSelectedOrder} changeId={orderChangeId} onChanged={reload} /> : <EmptyState title="Orders unavailable">Reload to view orders.</EmptyState>
      : <ProductsList api={api} siteId={siteId} shop={shop} language={language} reload={reload} onOpen={onProductId} />}
  </AreaPage>;
}
